export type DocumentationLink = {
  name?: string
  url?: string
  type?: string
}

export type AppItemSource = 'APP_STORE' | 'INTEGRATION'

export type AppItemStatus = 'active' | 'configured' | 'inactive'

export type TopLevelTab = 'all' | 'apps' | 'integrations' | 'manage'

export type ManageSubTab = 'apps' | 'integrations'

export type UnifiedAppItem = {
  id: string
  displayName: string
  description: string
  logoUrl: string
  category: string
  source: AppItemSource
  status: AppItemStatus
  enabled?: boolean
  redirectUrl?: string
  integrationConfigKey?: string
  rating?: number
  numberOfBrandUsing?: number
  isFree?: boolean
  hasFreeTrial?: boolean
  price?: string
  featurePriceSum?: number
  permissionId?: string
  navigatePath: string
  configPath?: string
  byShopflo?: boolean
  autoInstall?: boolean
  slug?: string
}

export type AppCategory = {
  key: string
  label: string
  subtitle?: string
}

export type GroupedCategory = {
  key: string
  label: string
  subtitle?: string
  items: UnifiedAppItem[]
}

export type ManageAppRow = {
  id: string
  displayName: string
  logoUrl: string
  pricing: string
  navigatePath: string
  redirectUrl?: string
  autoInstall?: boolean
}

export type ManageIntegrationRow = {
  id: string
  displayName: string
  logoUrl: string
  category: string
  categoryLabel: string
  active: boolean
  enabled: boolean
  configurePath: string
}

// ─── Featured / Popular partners ─────────────────────────────────────────────

export type FeaturedPartner = {
  id: string
  name: string
  slug: string
  logo: string
  short_description: string
  categories: string[]
  type: 'app' | 'integration'
  featured: boolean
  popular: boolean
  redirect_url: string
  /** Backend-driven install/usage status — drives the badge in AppCard */
  status?: AppItemStatus
  /** Star rating out of 5 */
  rating?: number
  /** Number of brands/merchants using this app */
  number_of_brands_using?: number
  /** Shows "Free trial" badge when true */
  has_free_trial?: boolean
  /** Optional banner image shown as card background in the featured slider */
  banner_image?: string
  /** Accent colour (hex or "r, g, b") used to tint the featured-card overlay gradient */
  accent_color?: string
  /** Optional label shown as a top-left badge, e.g. "ShopPass Exclusive" */
  badge?: string
  /** When true, shows ShopPass Exclusive badge on featured cards and Shopflo icon on normal cards */
  by_shopflo?: boolean
  /** AUTO_INSTALL apps surface as "Pre Installed" instead of "Installed". */
  auto_install?: boolean
}

// ─── V2 API types (flo-settlement /api/v2/apps) ──────────────────────────────

export type V2AppType = 'APP' | 'INTEGRATION'

export type V2AppCategory =
  | 'INCREASE_AOV'
  | 'INCREASE_CONVERSIONS'
  | 'INCREASE_PREPAID_SHARE'
  | 'INCREASE_LTV'
  | 'UTILITY'
  | 'REDUCE_RTO'
  | 'ADS_AND_ANALYTICS'
  | 'AOV_BOOSTER'
  | 'CONVERSION_BOOSTERS'
  | 'DISCOUNTING'
  | 'RETARGETING'
  | 'REWARDS_AND_LOYALTY'
  | 'SHIPPING'
  | 'THANKYOU_PAGE'

export type V2ConfigFieldType =
  | 'TEXT'
  | 'NUMBER'
  | 'TOGGLE'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'SECRET'
  | 'URL'
  | 'EMAIL'
  | 'PASSWORD'
  | 'TEXTAREA'
  | 'ARRAY'
  | 'GROUP'
  | 'KEY_VALUE_MAP'
  | 'CHANNEL_SELECTOR'
  | 'SEGMENTED_TOGGLE'

export type V2IntegrationConfigSource =
  | 'ANALYTICS_BACKEND'
  | 'SHIPPING_ENGINE'
  | 'LOYALTY_ENGINE'
  | 'ACCOUNT_ATTRIBUTES'
  | 'THANK_YOU_PAGE_SCRIPTS'
  | 'LOCAL'

export type V2AppMedia = {
  type?: string
  primary: boolean
  priority: number
  url?: string
  altText?: string
  width?: number
  height?: number
  metadata?: Record<string, unknown>
  tags?: string[]
}

export type V2ConfigFieldOption = {
  value: string
  label: string
}

export type V2ConfigField = {
  id: string
  label?: string
  type: V2ConfigFieldType
  required: boolean
  placeholder?: string
  helpText?: string
  defaultValue?: unknown
  hidden: boolean
  // Client-only: renders a '?' tooltip next to the field label.
  labelTooltip?: string
  options?: V2ConfigFieldOption[]
  validationRules?: Record<string, unknown>
  eventList: boolean
  children?: V2ConfigField[]
  minItems?: number
  maxItems?: number
}

export type V2ConfigSection = {
  id: string
  title?: string
  description?: string
  priority: number
  fields?: V2ConfigField[]
}

export type V2PricingConfig = {
  linkedPlanId?: string
  featurePriceMapping?: Record<string, number>
  meterStrategyMapping?: Record<string, unknown>
}

export type V2PricingTier = {
  id: string
  name?: string
  description?: string
  meter_strategy_mapping: Record<string, any>
  feature_price_mapping?: Record<string, number>
  marked_up_price?: number
  features: string[]
  priority: number
  restrictions?: Record<string, unknown>
  image_src: string
}

export type AppTierFeaturePlanConfig = Omit<V2PricingTier, 'meter_strategy_mapping'>

export type V2TrialPeriodConfig = {
  enabled: boolean
  defaultTierToInstall?: string
  unit?: string
  duration?: number
}

export type V2MerchantAppDetails = {
  appId: string
  active: boolean
  trialPeriodStatus?: string
  trialPeriodConfig?: V2TrialPeriodConfig
  pricingConfig?: V2PricingConfig
  installedOn?: string
  numDaysLeftOnTrial?: number
  markedUpPrice?: number
  metadata?: Record<string, unknown>
  currentPlan?: string
}

export type V2MerchantConfig = {
  configSectionId: string
  configValues: Record<string, unknown>
  active: boolean
}

/** Shape returned by GET /api/v2/apps (listing) */
export type V2UnifiedAppItem = {
  id: string
  name: string
  displayName?: string
  externalDescription?: string
  appType: V2AppType
  category?: V2AppCategory | null
  primaryMedia?: V2AppMedia
  rating?: number
  numberOfBrandsUsing?: number
  installed: boolean
  enabled?: boolean
  redirectUrl?: string
  byShopflo: boolean
  type?: string
  slug?: string
  integrationConfigKey?: string
  featuredBannerUrl?: string
  featuredTitle?: string
  featuredSubtitle?: string
  /** RGB tint for the featured-card overlay gradient, from `featured_accent_color` */
  featuredAccentColor?: string
  markedUpPrice?: number
  pricingTiers?: V2PricingTier[]
  isFree?: boolean
  merchantAppDetails?: V2MerchantAppDetails
}

/** Raw list item from GET /flo-settlement/api/v2/apps (snake_case JSON). */
export type V2AppsListPrimaryMediaApi = {
  type?: string
  primary?: boolean
  priority?: number
  url?: string | null
  alt_text?: string | null
  width?: number | null
  height?: number | null
  metadata?: Record<string, unknown> | null
  tags?: string[] | null
}

export type V2AppsListItemApi = {
  id: string
  name: string
  display_name?: string | null
  external_description?: string | null
  app_type: V2AppType
  category?: V2AppCategory | null
  primary_media?: V2AppsListPrimaryMediaApi | null
  rating?: number
  number_of_brands_using?: number
  installed: boolean
  enabled?: boolean | null
  redirect_url?: string | null
  by_shopflo: boolean
  type?: string
  slug?: string | null
  integration_config_key?: string | null
  featured_banner_url?: string | null
  featured_title?: string | null
  featured_subtitle?: string | null
  featured_accent_color?: string | null
  marked_up_price?: number | null
  pricing_tiers?: V2PricingTier[] | null
  is_free?: boolean | null
  merchant_app_details?: Record<string, unknown> | null
}

/** Shape returned by GET /api/v2/apps/{appId} (detail) */
export type V2UnifiedAppDetail = {
  id: string
  name: string
  displayName?: string
  description?: string
  externalDescription?: string
  features?: string
  medias?: V2AppMedia[]
  type?: string
  active: boolean
  installed?: boolean
  enabled?: boolean
  appType: V2AppType
  category?: V2AppCategory
  rating?: number
  numberOfBrandsUsing?: number
  brandDetails?: string
  trialPeriodConfig?: V2TrialPeriodConfig
  pricingConfig?: V2PricingConfig[]
  defaultPricingConfig?: V2PricingConfig
  pricingTiers?: V2PricingTier[]
  markedUpPrice?: number
  documentations?: { name?: string; url?: string; type?: string }[]
  reviews?: { reviewedBy?: string; reviewContent?: string; reviewerImage?: string; rating?: number }[]
  installationActions?: unknown[]
  metadata?: Record<string, unknown>
  redirectUrl?: string
  linkedTabName?: string
  externalSupportRequired?: boolean
  worksWith?: string[]
  calendlyLink?: string
  visible: boolean
  configSchema?: V2ConfigSection[]
  integrationConfigSource?: V2IntegrationConfigSource
  integrationConfigKey?: string
  setupInstructions?: string
  howItWorks?: string
  helpUrl?: string
  byShopflo: boolean
  slug?: string
  featuredTitle?: string
  featuredSubtitle?: string
  popular: boolean
  multiConfig: boolean
  createdAt?: number
  updatedAt?: number
  merchantAppDetails?: V2MerchantAppDetails
  merchantConfigs?: V2MerchantConfig[]
  externallyManaged: boolean
  isFree?: boolean
}

export type V2CategoryEntry = {
  category: V2AppCategory
  title: string
  subtitle?: string
}

export type V2ListAppsResponse = {
  items: V2UnifiedAppItem[]
  popular: V2UnifiedAppItem[]
  featured: V2UnifiedAppItem[]
  categories: (V2AppCategory | V2CategoryEntry)[]
}

export type UpsellPlanId = 'lite' | 'plus'

export type UpsellPlanOption = {
  id: UpsellPlanId
  label: string
  price: number
  originalPrice?: number
  discountLabel?: string
  description: string
  features: string[]
  highlighted?: boolean
}

export enum UpsellDialogKey {
  'plans' = 'plans',
  'cancellationWarning' = 'cancellationWarning',
  'degradePlan' = 'degradePlan',
  'error' = 'error'
}

/** Where the plan picker was opened from. */
export enum UpsellEntryPoint {
  APP_STORE = 'app_store',
  DOWNGRADE = 'downgrade',
  BANNER = 'banner'
}

/** Which Upsell dialog a plan-change click came from. */
export enum UpsellPlanActionSource {
  PLAN_MODAL = 'plan_modal',
  RESTRICTION_MODAL = 'restriction_modal'
}

/** Pulse activity-log action names for the Upsell plan dialogs (entity: EntityName.UPSELL). */
export enum UpsellActivityAction {
  PLAN_MODAL_OPENED = 'Plan modal opened',
  RESTRICTION_MODAL_SHOWN = 'Restriction modal shown',
  UPGRADE_CLICKED = 'Upgrade clicked',
  SWITCH_TO_LITE_CLICKED = 'Switch to lite clicked',
  UNINSTALL_CONFIRMED = 'Uninstall confirmed'
}

/** Which call hit FSE_001 — its downgrade-picker retry goes back through the same one. */
export enum DowngradeRetryVia {
  ACTIVATE = 'activate',
  TIER_UPDATE = 'tierUpdate'
}

export type UpsellDialogsState = {
  currentPlan: UpsellPlanId | null
  isLoading: boolean
  activeDialog: UpsellDialogKey | null
  planModalEntryPoint: UpsellEntryPoint
  isPlanModalReopened: boolean
  selectingPlanId: UpsellPlanId | null
  closeDialog: () => void
  handleSelectPlan: (planId: UpsellPlanId, selectedPlanIds?: string[]) => void
  handleContinueCancellation: () => void
  handleSwitchToLite: () => void
  handleDowngradeBack: () => void
}

export type UpsellTableData = {
  id: string
  title: string
  image_url: string
  created_at: string
  is_active: boolean
}

export type UpsellDowngradePlanDialogProps = {
  state: {
    isOpen: boolean
    upsells: UpsellTableData[]
    activeUpsellsCount: number
    isLoading?: boolean
    maxAllowedActiveUpsellsOnLite: number
  }
  actions: {
    /** Back arrow — returns to the dialog the picker was opened from. */
    onBack: () => void
    /** X / Escape — closes the flow. */
    onClose: () => void
    onContinue: (selectedUpsellIds: string[]) => void
  }
}

export type UpsellDialogsProps = {
  state: UpsellDialogsState
  upsells?: UpsellTableData[]
  activeUpsellsCount?: number
  onDegradeContinue?: (selectedUpsellIds: string[]) => void
}

export type AppIntegrationDetailViewProps = {
  /** The app's full detail — the view derives everything it renders from this. */
  detailConfig: V2UnifiedAppDetail
  backPath?: string
  additionalAppPath?: string
  onActivationChange?: () => void | Promise<void>
}

/** Generic body for PUT /apps/:id/tier — each app's own payload type (e.g. UpsellPayloadConfig) fits this shape. */
export type V2AppTierUpdatePayload = {
  current_tier_id?: string
  target_tier_id: string
  data?: Record<string, unknown>
}

export type AppMenuAction = {
  key: string
  label: string
  onClick: () => void
  disabled?: boolean
}

export type AppDetailSidebarProps = {
  name: string
  logoUrl?: string
  byShopflo: boolean
  autoInstall?: boolean
  categoryLabel?: string | null
  price?: number
  markedUpPrice?: number
  numberOfBrandsUsing?: number
  rating?: number
  reviewCount?: number
  isActive: boolean
  activating: boolean
  menuActions?: AppMenuAction[]
  activationError: string | null
  ctaLabel: string
  onCtaClick: () => void
  redirectUrl?: string
  onViewApp: () => void
  onManage: () => void
  worksWith?: string[]
  documentations?: DocumentationLink[]
  helpUrl?: string
  sidebarSlot?: React.ReactNode
}

export type DetailTab = 'about' | 'how_it_works' | 'configuration'

export type UpgradePitch = {
  icon: string
  title: string
  status: 'available' | 'coming_soon'
  description: string
  key: string
}

/** A pricing tier's `restrictions` for the Upsell app (shopfloApps pricing_tiers). */
export type UpsellTierRestrictions = {
  DISCOUNT_ALLOWED?: boolean
  AI_UPSELL_ALLOWED?: boolean
  ALLOWED_REAL_ESTATE?: string[]
  MAX_ACTIVE_UPSELL_GROUPS?: number
}

export type UpsellUpgradeConfig = {
  description: string
  upgrade_pitch: UpgradePitch[]
}

export type UpsellCancellationConfig = {
  title: string
  description: string
  downgrade_pitch: Pick<UpgradePitch, 'icon' | 'title' | 'description'>[]
}

export type UpsellUpgradeFeatureItem = UpgradePitch

/** How Upsell presents itself — the current plan's own name/logo when it has one, otherwise the app's. */
export type UpsellCosmetics = {
  name: string
  logoUrl: string
}

export type UpsellUpgradePricing = {
  price: number
  originalPrice?: number
  discountLabel?: string
  ctaLabel?: string
  footnote?: string
}

/** Which upgrade pitch item the dialog highlights — matched against `UpgradePitch.key`. */
export type UpsellUpgradeParameter = 'updated_active_upsells' | 'ai_upsell' | 'discount' | 'segment_targeting'

export type UpsellUpgradeDialogProps = {
  state: {
    isOpen: boolean
    isLoading?: boolean
    header?: React.ReactNode
    description?: string
    items?: UpsellUpgradeFeatureItem[]
    pricing?: UpsellUpgradePricing
    upgradeParameter?: UpsellUpgradeParameter
  }
  actions: {
    onClose: () => void
    onUpgrade: () => void
  }
}

export type UpsellNudgeTextConfig = {
  header: string
  description: string
}

export type UpsellSidebarNudgeProps = {
  variant?: 'sidebar'
  buttonConfig: {
    onUpgradeClick: () => void
    buttonText?: string
  }
  upgradePlan: UpsellPlanId
  textConfig?: UpsellNudgeTextConfig
  className?: string
}

export type UpsellHeaderNudgeProps = {
  variant: 'header'
  currentPlan?: UpsellPlanId | null
  onViewPlansClick: () => void
  textConfig?: UpsellNudgeTextConfig
  className?: string
  disabled?: boolean
}

export type UpsellUpgradeNudgeProps = UpsellSidebarNudgeProps | UpsellHeaderNudgeProps

export type UpsellActivationDialogProps = {
  state: {
    isOpen: boolean
    appName: string
    logoUrl: string
    currentPlan?: UpsellPlanId | null
    /** Plan tiers from the app's pricing_tiers — expected to contain only 'lite' / 'plus' ids. */
    plans: AppTierFeaturePlanConfig[]
    isLoading: boolean
    loadingPlanId?: UpsellPlanId | null
    /** Where the picker was opened from — logged when it opens. */
    entryPoint: UpsellEntryPoint
    /** Reopened via Back from the downgrade picker — the same visit, so the open isn't logged again. */
    isReopened?: boolean
  }
  actions: {
    onClose: () => void
    onSelectPlan: (planId: UpsellPlanId) => void
  }
}

export type ActiveUpsellApi = {
  id: string
  name: string
  is_active: boolean
  created_at: number
  metadata?: { entitledCatalogue?: { imageUrl?: string }[] }
}

export type TooManyActiveUpsellsErrorData = {
  restrictions?: { MAX_ACTIVE_UPSELL_GROUPS?: number }
  active_upsells?: ActiveUpsellApi[]
}

export type UpsellDegradeDialogData = {
  upsells: UpsellTableData[]
}

export type PendingAction = {
  kind: 'uninstall' | 'deactivate'
  id: string
  name: string
  redirectUrl?: string
  logoUrl?: string
}

export type UninstallCandidate = {
  redirectUrl?: string
  logoUrl?: string
  displayName?: string
}
